/**
 * Tools that step the editor's undo stack, the one Ctrl+Z steps. The stack
 * holds the user's own edits as well as the tools' (one step per call), so
 * each answer names what it took back or put back.
 */

import { getUndoManager } from '../undo/UndoManager';
import { PLAY_MODE_REFUSAL } from '../authoring/types';
import { defineTool, fail, refusalText, type ToolInput } from './input';
import type { SceneTool, SceneToolHost, ToolOutcome } from './types';

const MAX_STEPS = 20;

async function step(direction: 'undo' | 'redo', input: ToolInput, host: SceneToolHost): Promise<ToolOutcome> {
    const steps = input.steps ?? 1;
    if (typeof steps !== 'number' || !Number.isInteger(steps) || steps < 1 || steps > MAX_STEPS) {
        return fail(`Error: steps is a whole number from 1 to ${MAX_STEPS}.`);
    }
    const undo = getUndoManager();
    // Frozen while the scene plays: undo() would return without moving.
    if (undo.frozen) return fail(`Error: ${host.scene.isPlayMode() ? refusalText(host, PLAY_MODE_REFUSAL) : 'The undo stack is paused.'}`);
    const done: string[] = [];
    const verb = direction === 'undo' ? 'Undid' : 'Redid';
    let tried = false;
    try {
        for (let i = 0; i < steps; i++) {
            const label = direction === 'undo' ? undo.undoLabel : undo.redoLabel;
            // A frozen stack (the scene started playing during an await) would not move: stop rather than claim a step.
            if (label === null || undo.frozen) break;
            tried = true;
            await undo[direction]();
            done.push(`"${label}"`);
        }
    } catch (error) {
        const before = done.length ? `${verb} ${done.join(', ')}, then ` : '';
        const text = `${before}${direction === 'undo' ? 'undoing' : 'redoing'} failed: ${error instanceof Error ? error.message : String(error)}`;
        return done.length ? { text } : fail(`Error: ${text[0].toUpperCase()}${text.slice(1)}`);
    } finally {
        // A step that threw part-way may still have changed the scene.
        if (tried) host.changed?.();
    }
    if (!done.length) return { text: direction === 'undo' ? 'Nothing to undo.' : 'Nothing to redo.' };
    const left = direction === 'undo'
        ? (undo.canUndo ? `${undo.undoDepth} more step(s) can be undone` : 'nothing more to undo')
        : (undo.canRedo ? 'more steps can be redone' : 'nothing more to redo');
    const short = done.length < steps ? ` Only ${done.length} of ${steps} step(s) were there.` : '';
    return { text: `${verb} ${done.join(', ')}; ${left}.${short}` };
}

const STEPS_SCHEMA = { type: 'object', properties: { steps: { type: 'integer', minimum: 1, maximum: MAX_STEPS } } } as const;

const undoTool = defineTool({
    name: 'undo',
    verb: 'Undoing',
    edits: true,
    inputSchema: STEPS_SCHEMA,
    run: (input, host) => step('undo', input, host),
});

const redoTool = defineTool({
    name: 'redo',
    verb: 'Redoing',
    edits: true,
    inputSchema: STEPS_SCHEMA,
    run: (input, host) => step('redo', input, host),
});

export const UNDO_TOOLS: readonly SceneTool[] = [undoTool, redoTool];
